<script setup>
import { ref, computed, nextTick, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  modelValue: {
    type: File,
    default: null,
  },
  label: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  messageType: {
    type: String,
    default: 'info',
    validator: value => ['info', 'error', 'success'].includes(value),
  },
  // 已有的 logo URL（用于编辑时显示）
  existingLogoUrl: {
    type: String,
    default: '',
  },
  // 翻译键前缀（例如：'PILOT.KNOWLEDGE_BASES.FORM.LOGO' 或 'PILOT.FAQS.FORM.LOGO'）
  translationPrefix: {
    type: String,
    default: 'PILOT.KNOWLEDGE_BASES.FORM.LOGO',
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const logoPreviewUrl = ref(null);
const fileInputRef = ref(null);
const uploadError = ref('');

const hasError = computed(
  () => props.messageType === 'error' || !!uploadError.value
);

const displayMessage = computed(() => {
  if (uploadError.value) return uploadError.value;
  return props.message;
});

const messageClass = computed(() => {
  if (hasError.value) {
    return 'text-n-ruby-9 dark:text-n-ruby-9';
  }
  return 'text-n-slate-11 dark:text-n-slate-11';
});

const borderClass = computed(() => {
  if (hasError.value) {
    return 'border-n-ruby-8 dark:border-n-ruby-8';
  }
  return 'border-n-slate-3 dark:border-n-slate-7';
});

// 显示的 logo URL：优先显示新上传的预览，否则显示已有的 logo
const displayLogoUrl = computed(() => {
  return logoPreviewUrl.value || props.existingLogoUrl;
});

const handleFileChange = event => {
  const file = event.target.files[0];
  if (!file) {
    return;
  }

  // 清除之前的错误
  uploadError.value = '';

  // 验证文件类型
  if (!ACCEPTED_TYPES.includes(file.type)) {
    uploadError.value = t(`${props.translationPrefix}.INVALID_TYPE`);
    clearFile();
    return;
  }

  // 验证文件大小
  if (file.size > MAX_FILE_SIZE) {
    uploadError.value = t(`${props.translationPrefix}.TOO_LARGE`);
    clearFile();
    return;
  }

  // 保存文件
  emit('update:modelValue', file);

  // 创建预览URL
  if (logoPreviewUrl.value) {
    URL.revokeObjectURL(logoPreviewUrl.value);
  }
  logoPreviewUrl.value = URL.createObjectURL(file);
};

const openFileDialog = () => {
  nextTick(() => {
    if (fileInputRef.value) {
      fileInputRef.value.click();
    }
  });
};

const removeLogo = () => {
  emit('update:modelValue', null);
  uploadError.value = '';
  clearFile();
};

const clearFile = () => {
  if (logoPreviewUrl.value) {
    URL.revokeObjectURL(logoPreviewUrl.value);
    logoPreviewUrl.value = null;
  }
  if (fileInputRef.value) {
    fileInputRef.value.value = '';
  }
};

const cleanup = () => {
  if (logoPreviewUrl.value) {
    URL.revokeObjectURL(logoPreviewUrl.value);
    logoPreviewUrl.value = null;
  }
};

// 重置组件状态（供父组件调用）
const reset = () => {
  clearFile();
  uploadError.value = '';
};

onUnmounted(() => {
  cleanup();
});

defineExpose({ reset });
</script>

<template>
  <div class="w-full">
    <label class="mb-0.5 text-sm font-medium text-n-slate-12">
      {{ label }}
    </label>
    <div class="flex items-center gap-4">
      <!-- 预览区域 - 支持点击上传 -->
      <div
        class="w-20 h-20 rounded-lg border-2 border-dashed flex items-center justify-center overflow-hidden bg-n-slate-2 dark:bg-n-slate-8 flex-shrink-0 cursor-pointer hover:bg-n-slate-3 dark:hover:bg-n-slate-7 transition-colors"
        :class="borderClass"
        @click="openFileDialog"
      >
        <img
          v-if="displayLogoUrl"
          :src="displayLogoUrl"
          alt="Logo preview"
          class="w-full h-full object-cover"
        />
        <i
          v-else
          class="i-lucide-image text-2xl text-n-slate-5 dark:text-n-slate-6"
        />
      </div>

      <!-- 上传按钮区域 -->
      <div class="flex-1">
        <input
          ref="fileInputRef"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          class="hidden"
          @change="handleFileChange"
        />
        <div class="flex gap-2">
          <Button
            type="button"
            variant="faded"
            color="slate"
            :label="t(`${translationPrefix}.UPLOAD_BUTTON`)"
            @click="openFileDialog"
          />
          <Button
            v-if="modelValue"
            type="button"
            color="ruby"
            variant="outline"
            :label="t(`${translationPrefix}.REMOVE_BUTTON`)"
            @click="removeLogo"
          />
        </div>
        <p class="mt-2 text-xs text-n-slate-11 dark:text-n-slate-7">
          {{ t(`${translationPrefix}.HELP_TEXT`) }}
        </p>
      </div>
    </div>
    <!-- 错误提示 -->
    <p v-if="displayMessage" class="mt-1.5 text-xs" :class="[messageClass]">
      {{ displayMessage }}
    </p>
  </div>
</template>
